import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ComplexAction,
  Modal as ShopifyModal,
  ModalProps as ShopifyModalProps,
} from '@shopify/polaris';
import { showHubspotChatBubbleState } from '@states';
import { useRecoilState } from 'recoil';
import styled, { createGlobalStyle, css } from 'styled-components';
import { clearClassList, findParentByClass } from '@utils/dom';

export type ModalRef = {
  hideModal: () => void;
  showModal: () => void;
  updateContent: (content: () => React.ReactNode) => void;
  updateProps: (props: Partial<ModalState>) => void;
  display: (
    content: () => React.ReactNode,
    props?: Partial<ModalState>,
  ) => void;
};

export type ModalProps = {
  onRef: (ref: ModalRef) => void;
};

export type ModalAction = {
  withNestedModal?: boolean;
};

export type ModalState = Omit<
  ShopifyModalProps,
  'open' | 'secondaryActions' | 'primaryAction'
> & {
  primaryAction?: ShopifyModalProps['primaryAction'] & ModalAction;
  secondaryAction?: ShopifyModalProps['primaryAction'] & ModalAction;
  otherActions?: ShopifyModalProps['secondaryActions'] & ModalAction;
  styled?: ReturnType<typeof css>;
};

const ModalParent = styled.div``;

const ModalParentStyle = createGlobalStyle<{
  parentStyled: ModalState['styled'];
}>`
  ${ModalParent} {
    ${({ parentStyled }) => parentStyled}
  }
`;

const initialProps = (): ModalState => ({
  onClose: () => ({}),
  title: '',
});

const polarisModalCssClass = 'Polaris-Modal-Dialog__Modal';

export const modalWithSpacedButtons = css`
  .Polaris-Stack__Item {
    width: 100%;
  }
  .Polaris-ButtonGroup {
    width: 100%;
    justify-content: space-between;
  }

  .Polaris-Stack__Item.Polaris-Stack__Item--fill {
    display: none;
  }

  .Polaris-ButtonGroup__Item:last-child {
    margin-right: 0.8rem;
  }

  .Polaris-ButtonGroup__Item:first-child .Polaris-Button {
    border: none;
    padding: 0;
    box-shadow: none;

    &:hover {
      background: transparent;
    }
  }
`;

const Modal: FunctionComponent<ModalProps> = ({ onRef }) => {
  const childRef = useRef<HTMLElement>();
  const [active, setActive] = useState(false);
  const [content, setContent] = useState<() => React.ReactNode>();
  const [modalProps, setModalProps] = useState<ModalState>(initialProps());
  const [, setShowHubspotChatBubble] = useRecoilState(
    showHubspotChatBubbleState,
  );

  const onCloseModal = useCallback(() => {
    setActive(false);
    setContent(undefined);
    setModalProps(initialProps());
    setShowHubspotChatBubble(false);
    if (modalProps.onClose) {
      modalProps.onClose();
    }
  }, [modalProps, setShowHubspotChatBubble]);

  const hideModal = useCallback(() => onCloseModal(), [onCloseModal]);

  const showModal = useCallback(() => {
    setActive(true);
    setShowHubspotChatBubble(false);
  }, [setShowHubspotChatBubble]);

  function onChildRef(ref: HTMLElement | null) {
    if (ref) {
      const parentModal = findParentByClass(ref, polarisModalCssClass);
      if (parentModal) {
        if (ref.className) {
          parentModal.classList.add(...ref.className.split(' '));
          clearClassList(ref);
        }
        childRef.current = parentModal;
      }
    }
  }

  const actionAndClose = useCallback(
    (fn?: () => void) => () => {
      if (fn) {
        fn();
      }
      hideModal();
    },
    [hideModal],
  );

  const updateProps = useCallback(
    (props: Partial<ModalState>) => {
      const result: Partial<ModalState> = { ...props };
      if (result.primaryAction) {
        result.primaryAction = {
          ...result.primaryAction,
          onAction:
            result.primaryAction.withNestedModal &&
            result.primaryAction?.onAction
              ? result.primaryAction?.onAction
              : actionAndClose(result.primaryAction?.onAction),
        };
      }
      if (result.secondaryAction) {
        result.secondaryAction = {
          ...result.secondaryAction,
          onAction:
            result.secondaryAction.withNestedModal &&
            result.secondaryAction?.onAction
              ? result.secondaryAction?.onAction
              : actionAndClose(result.secondaryAction?.onAction),
        };
      }

      setModalProps(result as ModalState);
    },
    [actionAndClose],
  );

  const display = useCallback(
    (content: () => React.ReactNode, props: Partial<ModalState> = {}) => {
      updateContent(content);
      updateProps(props);
      showModal();
    },
    [showModal, updateProps],
  );

  useEffect(() => {
    onRef({
      display,
      hideModal,
      showModal,
      updateContent,
      updateProps,
    });
  }, [display, hideModal, onRef, showModal, updateProps]);

  function updateContent(content: () => React.ReactNode) {
    setContent(content);
  }

  const secondaryAction: ComplexAction[] = modalProps.otherActions || [];

  if (modalProps.secondaryAction) {
    secondaryAction.push(modalProps.secondaryAction);
  }

  return (
    <ShopifyModal
      open={active}
      secondaryActions={secondaryAction}
      {...modalProps}
      onClose={onCloseModal}
    >
      <ShopifyModal.Section>
        <ModalParentStyle parentStyled={modalProps.styled} />
        <ModalParent ref={onChildRef}>{content}</ModalParent>
      </ShopifyModal.Section>
    </ShopifyModal>
  );
};

export default Modal;
